import React, { memo } from 'react';
import { useProjectListView } from '../project-list-view.context';
import { useUIState } from '@client/store/ui';
import { ElementListTree } from '@features/proposals/ElementListRow/ElementListTree';
import {
  PROJECT_MAP_CARD_HEIGHT,
  PROJECT_MAP_CARD_GAP,
  PROJECT_LIST_ROW_HEIGHT,
} from './project-list.constants';
import { isProjectInfo } from '@shared/helpers/project-folder.helpers';
import { OneOfProjectListElements } from '@shared/models/project.model';
import { HideOutsideViewport } from '@ui/HideOutsideViewport';
import { ProjectListItem } from './ProjectListItem';
import { Box } from '@mui/material';
import { ELEMENT_LIST_WIDTH_CSS } from '@features/proposals/ElementListRow/element-list-column-resize.hook';
import { useProjectDragScroll } from '../use-project-drag-scroll.hook';

export const ProjectListTree = ({
  items,
  mapColumns = 1,
  mapWidth,
}: {
  items: OneOfProjectListElements[];
  mapColumns?: number;
  mapWidth?: number;
}) => {
  const { view } = useProjectListView();
  const { dragging, ...dragHandlers } = useProjectDragScroll({
    axis: 'y',
    scrollContainerSelector: '[data-project-list-page]',
  });
  return (
    <Box
      {...dragHandlers}
      style={{ userSelect: 'none', cursor: dragging ? 'grabbing' : undefined }}
      sx={
        view === 'Map'
          ? {
              width: mapWidth,
              pointerEvents: 'auto',
              // The fixed header's 1 px bottom border covers the first pixel.
              pt: '9px',
              '& > [role="tree"]': {
                display: 'grid',
                paddingX: '8px',
                columnGap: `${PROJECT_MAP_CARD_GAP}px`,
                gridTemplateColumns: `repeat(${mapColumns}, minmax(0, 1fr))`,
              },
            }
          : { pt: '9px' }
      }
    >
      <Box
        data-project-list-column=""
        data-project-map-columns={mapColumns}
        sx={{
          width: mapWidth ?? ELEMENT_LIST_WIDTH_CSS,
          height: 0,
          pointerEvents: 'none',
        }}
      />
      <ElementListTree aria-label="Projects">
        {items.filter(isProjectInfo).map((item) => (
          <Box key={item.id} data-project-list-row-id={item.id}>
            <ProjectTreeRow item={item} />
          </Box>
        ))}
      </ElementListTree>
    </Box>
  );
};

const ProjectTreeRow = memo(({ item }: { item: OneOfProjectListElements }) => {
  const { addedElementId, observedElementIds } = useUIState(
    'addedElementId',
    'observedElementIds',
  );
  const { view } = useProjectListView();
  const isLastAdded = addedElementId === item.id;
  return (
    <HideOutsideViewport
      cullBeforeMount
      height={
        view === 'Map'
          ? PROJECT_MAP_CARD_HEIGHT + PROJECT_MAP_CARD_GAP
          : PROJECT_LIST_ROW_HEIGHT + PROJECT_MAP_CARD_GAP
      }
      alwaysVisible={observedElementIds[item.id]}
      scrollTo={isLastAdded}
    >
      <ProjectListItem item={item} indentation={0} autoFocus={isLastAdded} />
    </HideOutsideViewport>
  );
});
